// Port of @mm56/vitevue webgl/plugins/time/timePlugin as a standalone singleton.
// Provides dt, qualityDt, stableDt, averageDt, clampedDt, elapsed, frameNum.

// Hz detection: every 10hz from 30 to 360
const HZ_TARGETS = []
for (let hz = 30; hz <= 360; hz += 10) HZ_TARGETS.push(hz)

const HZ_INTERVALS = new Float64Array(HZ_TARGETS.length)
for (let i = 0; i < HZ_TARGETS.length; i++) HZ_INTERVALS[i] = 1000 / HZ_TARGETS[i]

function snapToNearestHz(intervalMs) {
  let bestHz = HZ_TARGETS[0]
  let bestDiff = Infinity
  for (let i = 0; i < HZ_TARGETS.length; i++) {
    const diff = Math.abs(intervalMs - HZ_INTERVALS[i])
    if (diff < bestDiff) { bestDiff = diff; bestHz = HZ_TARGETS[i] }
  }
  return bestHz
}


function createMovingAverage(maxSize) {
  const buffer = new Float64Array(maxSize)
  let sum = 0, index = 0, count = 0

  return {
    push(value) {
      if (count < maxSize) count++
      index = (index + 1) % maxSize
      const prev = buffer[index]
      buffer[index] = value
      sum += value - prev
      return sum / count
    },
    reset() {
      buffer.fill(0)
      sum = index = count = 0
    },
    get value() { return count > 0 ? sum / count : 0 }
  }
}


class TimeManager {

  constructor() {
    this.dt = 0              // raw deltaTime ms
    this.qualityDt = 0       // moving avg (6 frames) — used by QualityManager
    this.clampedDt = 0       // clamped to [1, 130] ms
    this.averageDt = 16.667  // moving avg (20 frames)
    this.stableDt = 16.667   // 1000 / detectedHz
    this.elapsed = 0         // total elapsed ms
    this.playingElapsed = 0  // elapsed minus paused time
    this.frameNum = 0

    this.clampFps = 0        // 0 = no fps clamp
    this.isPaused = false
    this.isStarted = false

    this.beforeObservers = []
    this.observers = []
    this.afterObservers = []
    this.rafHandler = null
    this.lastDate = null
    this.needsReset = false

    this.detectedHz = 60
    this.hzStable = false
    this.hzBuffer = new Float64Array(120)
    this.hzSortBuf = new Float64Array(120)
    this.hzBufIndex = 0
    this.hzBufCount = 0
    this.hzCheckCount = 0

    this.avgDt = createMovingAverage(20)
    this.avgQualityDt = createMovingAverage(6)
    this.clampDtAverage = createMovingAverage(20)
    this.clampDtAccum = 0
    this.clampDtFramesAccum = 0
    this.clampSkippedAccum = 0

    this.frame = this.frame.bind(this)
  }


  // Detected display refresh rate (snapped to nearest 10hz step).
  get stableFps() {
    return this.detectedHz
  }


  addBefore(fn) {
    if (!fn || this.beforeObservers.includes(fn)) return
    this.beforeObservers.push(fn)
  }

  removeBefore(fn) {
    const i = this.beforeObservers.indexOf(fn)
    if (i !== -1) this.beforeObservers.splice(i, 1)
  }


  add(fn) {
    if (!fn || this.observers.includes(fn)) return
    this.observers.push(fn)
  }

  remove(fn) {
    const i = this.observers.indexOf(fn)
    if (i !== -1) this.observers.splice(i, 1)
  }


  addAfter(fn) {
    if (!fn || this.afterObservers.includes(fn)) return
    this.afterObservers.push(fn)
  }

  removeAfter(fn) {
    const i = this.afterObservers.indexOf(fn)
    if (i !== -1) this.afterObservers.splice(i, 1)
  }


  start() {
    if (this.isStarted) return
    this.isStarted = true
    this.lastDate = null
    this.rafHandler = requestAnimationFrame(this.frame)
  }


  stop() {
    if (!this.isStarted) return
    this.isStarted = false
    if (this.rafHandler) cancelAnimationFrame(this.rafHandler)
    this.rafHandler = null
  }


  reset() {
    this.needsReset = true
  }


  updateDetectedHz() {
    const n = this.hzBufCount
    for (let i = 0; i < n; i++) this.hzSortBuf[i] = this.hzBuffer[i]
    this.hzSortBuf.subarray(0, n).sort()
    const mid = n >> 1
    const median = n % 2 === 0
      ? (this.hzSortBuf[mid - 1] + this.hzSortBuf[mid]) / 2
      : this.hzSortBuf[mid]
    this.detectedHz = snapToNearestHz(median)
    if (this.hzBufCount >= 60) this.hzStable = true
  }


  frame(now) {
    if (this.lastDate === null) this.lastDate = now
    let dt = now - this.lastDate
    if (dt === 0) dt = 16.667
    this.lastDate = now

    this.rafHandler = requestAnimationFrame(this.frame)

    if (this.needsReset) {
      this.needsReset = false
      dt = 16.667
      this.avgDt.reset()
      this.avgQualityDt.reset()
      this.clampDtAverage.reset()
      this.clampDtAccum = this.clampDtFramesAccum = this.clampSkippedAccum = 0
      this.hzBufIndex = this.hzBufCount = this.hzCheckCount = 0
    }

    // Optional FPS clamp (same algorithm as mm56 timePlugin)
    if (this.clampFps > 0) {
      const clampDt = this.clampDtAverage.push(dt)
      const target = 1000 / this.clampFps
      const delta = Math.max(0, target - clampDt)
      this.clampDtAccum += delta
      if (this.clampDtAccum >= target) {
        this.clampDtFramesAccum = 0
        this.clampDtAccum = Math.max(0, this.clampDtAccum - target)
        this.clampSkippedAccum += dt
        return
      }
      if (this.clampDtFramesAccum >= 10) {
        this.clampDtAccum = this.clampDtFramesAccum = 0
      } else {
        this.clampDtFramesAccum++
      }
      dt += this.clampSkippedAccum
      this.clampSkippedAccum = 0
    }

    // Compute all dt variants
    this.qualityDt = this.avgQualityDt.push(dt)
    this.clampedDt = Math.max(1, Math.min(130, dt))
    this.averageDt = this.avgDt.push(dt)

    // Hz detection: collect raw intervals, check every 30 frames
    this.hzBuffer[this.hzBufIndex] = dt
    this.hzBufIndex = (this.hzBufIndex + 1) % 120
    if (this.hzBufCount < 120) this.hzBufCount++
    if (++this.hzCheckCount >= 30) {
      this.hzCheckCount = 0
      this.updateDetectedHz()
    }
    this.stableDt = 1000 / this.detectedHz

    this.dt = dt
    this.elapsed += dt
    this.frameNum = (this.frameNum + 1) % Number.MAX_SAFE_INTEGER

    if (!this.isPaused) this.playingElapsed += dt

    const before = this.beforeObservers
    for (let i = 0, l = before.length; i < l; i++) before[i](this)

    const obs = this.observers
    for (let i = 0, l = obs.length; i < l; i++) obs[i](this)

    const after = this.afterObservers
    for (let i = 0, l = after.length; i < l; i++) after[i](this)
  }

}


export const Time = new TimeManager()
export default Time
